import React from 'react';
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Container,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';

import type { ApplicationStickyHeaderProps } from './types/applicationStickyHeader.types';
import { getApplicationStatusColor } from '../../../../constants/statusColors';

import { useApplicationCopy } from './hooks/useApplicationCopy';
import { useStatusManagement } from './hooks/useStatusManagement';
import { useTrackingManagement } from './hooks/useTrackingManagement';
import { useApplicationDelete } from './hooks/useApplicationDelete';

import { HeaderActions } from './components/HeaderActions';
import { ApplicationInfoSection } from './components/ApplicationInfoSection';
import { DeviceStatusSection } from './components/DeviceStatusSection';
import { StatusManagementSection } from './components/StatusManagementSection';
import { TrackingDialog } from './dialogs/TrackingDialog';
import { DeleteConfirmDialog } from './dialogs/DeleteConfirmDialog';
import { AlertPreviewDialog } from './dialogs/AlertPreviewDialog';

const ApplicationStickyHeader: React.FC<ApplicationStickyHeaderProps> = ({
  application,
  onBack,
  onRefresh,
  onStatusChange,
  onDelete,
  onTrackingUpdate,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const statusColor = getApplicationStatusColor(application.status);
  const deviceCount = application.devices?.length || 0;
  
  const copyHook = useApplicationCopy();

  const statusHook = useStatusManagement(
    application,
    onStatusChange,
    () => {
      // 저장 후 UI 최신화: 상위에서 onRefresh가 주어졌다면 호출
      copyHook.setSnackbarOpen(true);
      if (onRefresh) onRefresh();
    }
  );

  const trackingHook = useTrackingManagement(
    application,
    onTrackingUpdate,
    () => copyHook.setSnackbarOpen(true)
  );

  const deleteHook = useApplicationDelete(onDelete);

  const getSnackbarMessage = (): string => {
    if (copyHook.copiedField) {
      return '클립보드에 복사되었습니다!';
    }
    if (trackingHook.lastTrackingAction) {
      const actionType = trackingHook.lastTrackingAction.type;
      return `${actionType === 'delivery' ? '택배송장' : '반품 택배송장'}번호가 저장되었습니다!`;
    }
    return `상태가 성공적으로 변경되었습니다!${statusHook.sendAlert && statusHook.alertPreviewMessage ? ' (알림톡 발송 완료)' : ''}`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '0 0 24px 24px',
        border: '1px solid #e1e8ed',
        borderTop: 'none',
        bgcolor: '#fff',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(20px)',
        background: 'linear-gradient(135deg, #fff 0%, #f8fafd 100%)',
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <HeaderActions
            application={application}
            loading={loading}
            isDeleting={deleteHook.deleting}
            onBack={onBack}
            onRefresh={onRefresh}
            onDelete={onDelete}
            onDeleteClick={deleteHook.handleDeleteClick}
          />

          <Grid container spacing={3}>
            <ApplicationInfoSection
              application={application}
              copiedField={copyHook.copiedField}
              getCopyIconStyles={copyHook.getCopyIconStyles}
              getCopyTooltipTitle={copyHook.getCopyTooltipTitle}
              onCopy={copyHook.handleCopy}
              onOpenTrackingDialog={trackingHook.handleOpenTrackingDialog}
            />

            <DeviceStatusSection
              application={application}
              statusColor={statusColor}
              deviceCount={deviceCount}
              copiedField={copyHook.copiedField}
              getCopyIconStyles={copyHook.getCopyIconStyles}
              getCopyTooltipTitle={copyHook.getCopyTooltipTitle}
              onCopy={copyHook.handleCopy}
            />

            <StatusManagementSection
              application={application}
              tempStatus={statusHook.tempStatus}
              isStatusChanged={statusHook.isStatusChanged}
              isStatusSaving={statusHook.isStatusSaving}
              sendAlert={statusHook.sendAlert}
              alertPreviewMessage={statusHook.alertPreviewMessage}
              canSendAlert={statusHook.canSendAlert}
              selectedDeviceIds={(statusHook as any).selectedDeviceIds}
              perDevicePreviews={(statusHook as any).perDevicePreviews}
              onToggleDevice={(statusHook as any).toggleDeviceSelection}
              onToggleAll={(statusHook as any).toggleAllSelection}
              onStatusChange={onStatusChange}
              onStatusChangeHandler={statusHook.handleStatusChange}
              onStatusSave={statusHook.handleStatusSave}
              onStatusReset={statusHook.handleStatusReset}
              onAlertCheckChange={statusHook.handleAlertCheckChange}
              onPreviewOpen={statusHook.handlePreviewOpen}
              onInfoRequestSelect={statusHook.handleInfoRequestSelect}
            />
          </Grid>
        </Box>
      </Container>

      <Snackbar
        open={copyHook.snackbarOpen}
        autoHideDuration={2000}
        onClose={() => copyHook.setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => copyHook.setSnackbarOpen(false)}
          severity="success"
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {getSnackbarMessage()}
        </Alert>
      </Snackbar>

      <TrackingDialog
        open={trackingHook.trackingDialogOpen}
        trackingType={trackingHook.trackingType}
        trackingNumber={trackingHook.trackingNumber}
        trackingSubmitting={trackingHook.trackingSubmitting}
        onClose={() => trackingHook.setTrackingDialogOpen(false)}
        onTrackingNumberChange={trackingHook.setTrackingNumber}
        onSave={trackingHook.handleSaveTracking}
      />

      {/* 🗑️ 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        open={deleteHook.open}
        application={application}
        isDeleting={deleteHook.deleting}
        onClose={deleteHook.handleDeleteCancel}
        onConfirm={deleteHook.handleDeleteConfirm}
      />

      {/* 👁️ 알림톡 미리보기 다이얼로그 (상태 변경 + 정보입력요청 공용) */}
      <AlertPreviewDialog
        open={statusHook.alertPreviewOpen}
        application={application}
        alertPreviewMessage={statusHook.alertPreviewMessage}
        perDevicePreviews={(statusHook as any).tempStatus === '가격안내' ? (statusHook as any).perDevicePreviewsSelected : (statusHook as any).perDevicePreviews}
        isStatusSaving={statusHook.isStatusSaving}
        onClose={statusHook.handlePreviewClose}
        onConfirm={async () => {
          // 🆕 정보입력요청 모드인 경우 별도 처리
          if (statusHook.isInfoRequestMode) {
            await statusHook.executeInfoRequest();
          } else {
            statusHook.handlePreviewClose();
            await statusHook.executeStatusSave(true);
          }
        }}
        isInfoRequest={statusHook.isInfoRequestMode}
      />
    </Paper>
  );
};

export default ApplicationStickyHeader;